import { useState } from "react";
import { Copy, Share, PlusSquare, MoreVertical, MoreHorizontal, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { isInAppBrowser, isIOS, isMobile } from "@/lib/install";

const iosOtherBrowser = () => /crios|fxios|edgios|opios/i.test(window.navigator.userAgent);

const stepsFor = () => {
  if (isInAppBrowser()) {
    const app = isIOS() ? "Safari" : "Chrome";
    return {
      title: `Open in ${app} first`,
      note: "Instagram, Facebook and TikTok can't install apps. Open this page in your phone's browser:",
      copy: true,
      steps: [
        [MoreHorizontal, `Tap the ••• menu (top right corner of this screen).`],
        [Share, `Choose "Open in ${isIOS() ? "Safari" : "browser"}". No option? Tap Copy link below and paste it into ${app}.`],
        [PlusSquare, `In ${app}, tap "Get the App" again and follow the steps.`],
      ],
    };
  }
  if (isIOS()) {
    const bar = iosOtherBrowser() ? "in the address bar (top right)" : "at the bottom of Safari (on iPad: top right)";
    return {
      title: "Add JayPanduh to your iPhone",
      note: "iPhones install apps from the browser in 3 quick taps:",
      steps: [
        [Share, `Tap the Share button (square with an arrow ↑) ${bar}.`],
        [PlusSquare, `Scroll down and tap "Add to Home Screen". Don't see it? Scroll to the bottom, tap "Edit Actions" and add it.`],
        [Check, `Make sure "Open as Web App" is on (if shown), then tap "Add". JayPanduh appears on your home screen.`],
      ],
    };
  }
  if (isMobile()) {
    return {
      title: "Add JayPanduh to your phone",
      note: "In Chrome:",
      steps: [
        [MoreVertical, "Tap the ⋮ menu (top right)."],
        [PlusSquare, `Tap "Install app" or "Add to Home screen".`],
        [Check, `Tap "Install". JayPanduh appears on your home screen.`],
      ],
    };
  }
  return {
    title: "Get the JayPanduh app",
    note: "Open jaypanduh.com on your phone and tap Get the App, or install it here:",
    steps: [[PlusSquare, "Click the install icon at the right end of your browser's address bar."]],
  };
};

export const InstallGuide = ({ open, onOpenChange }) => {
  const [copied, setCopied] = useState(false);
  const g = stepsFor();
  const copy = async () => {
    try { await navigator.clipboard.writeText(window.location.origin); setCopied(true); } catch { setCopied(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="install-guide" className="bg-[#121216] border-[#56ED4A]/40 w-[94vw] max-w-md">
        <img src="/icon-v2-192.png" alt="" className="w-16 h-16" />
        <DialogTitle className="font-display uppercase text-3xl tracking-normal">{g.title}</DialogTitle>
        <p className="text-sm text-zinc-300 -mt-2">{g.note}</p>
        <ol className="flex flex-col gap-3">
          {g.steps.map(([Icon, text], n) => (
            <li key={text} data-testid={`install-guide-step-${n}`} className="flex gap-3 items-start border border-white/10 p-3">
              <span className="w-8 h-8 shrink-0 grid place-items-center bg-[#56ED4A] text-black font-mono font-semibold">{n + 1}</span>
              <span className="text-sm text-zinc-200 leading-snug flex-1">{text}</span>
              <Icon size={20} className="text-[#56ED4A] shrink-0 mt-1" />
            </li>
          ))}
        </ol>
        {g.copy && (
          <button data-testid="install-guide-copy-btn" onClick={copy} className="inline-flex items-center justify-center gap-2 border border-white/30 py-3 font-mono text-xs uppercase tracking-[0.2em]">
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Link copied" : "Copy link"}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
};
